import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { type SelectedStaff, WhoLabel } from "@/components/AddNurse/AddStaffFooter";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { FONT_SIZE, INK, LETTER_SPACING, NEUTRAL, STAFF_PICKER } from "@/theme/tokens";
import { ShiftWindowIcon } from "@/ui/Icons/ShiftWindowIcon";
import { InlineHint } from "@/ui/molecules/InlineHint";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { RemoveStaffExtraFields, RemoveStaffReasonField } from "./RemoveStaffDetailsFields";
import type { RemoveStaffSummary } from "./removeStaffUtils";
import type { RemoveStaffDetails } from "./useRemoveStaffDetails";

type Props = {
	details: RemoveStaffDetails;
	summary: RemoveStaffSummary;
	/** The picks, in pick order — named in a tooltip on "3 staff" so the count is checkable. */
	selectedStaff: readonly SelectedStaff[];
	/** The union-member warning for the picks, already translated; `null` when none of them is one. */
	unionWarning: string | null;
	confirmLabel: string;
	canSubmit: boolean;
	confirmLoading: boolean;
	onCancel: () => void;
	onConfirm: () => void;
};

/** A field's error line, in spacing units: the reason's "Pick a reason" hangs this far under the action row. */
const ERROR_LINE_GAP = 3;
/** The footer's gap between its rows at rest. */
const FOOTER_ROW_GAP = 1.5;

/**
 * The action row: legend · reason · read-back · buttons in one line at desktop widths. The read-back gives up
 * its room first (down to its floor), then the buttons wrap under it. The row gap is a field's error line tall,
 * so the reason's hanging "Pick a reason" never lands on a wrapped line below it.
 */
const ACTION_ROW_SX = { display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 2, rowGap: ERROR_LINE_GAP } as const;

const Separator: FC = () => (
	<Box component="span" aria-hidden sx={{ color: NEUTRAL[450], marginInline: 1 }}>
		·
	</Box>
);

/** Joins the one pick's name, date and hours in the read-back's tooltip, as the line's own "·" does. */
const FULL_LINE_SEPARATOR = " · ";

/**
 * "REMOVING / Brittany Frantz · Tue, Sep 22 ☀ 8:00 AM–3:00 PM" — what the button is about to do, read back. One
 * line that ellipsizes in a narrow row; with several picks the names stay behind the "3 staff" tooltip, and with
 * one pick the whole line is behind a tooltip, so a long name can't hide the date and hours for good.
 */
const RemovingReadBack: FC<Pick<Props, "summary" | "selectedStaff">> = ({ summary, selectedStaff }) => {
	const { t } = useTranslation();
	const line = (
		// Block + inline children, not flex: `text-overflow` only ellipsizes an inline formatting context.
		<Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: FONT_SIZE.EMPHASIS, color: INK.SECONDARY }}>
			<WhoLabel who={summary.who} hasSelection={selectedStaff.length > 0} selectedStaff={selectedStaff} testIdPrefix="remove-staff-summary" />
			<Separator />
			<span>{summary.date}</span>
			{summary.hours && summary.shiftIndex !== null && (
				<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, marginInlineStart: 1, verticalAlign: "middle" }}>
					<ShiftWindowIcon shiftIndex={summary.shiftIndex} labelled />
					<Box component="span" sx={{ fontWeight: 600, color: INK.PRIMARY, fontVariantNumeric: "tabular-nums" }}>
						{summary.hours}
					</Box>
				</Box>
			)}
		</Box>
	);
	return (
		<Box
			sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, flex: `1 1 ${STAFF_PICKER.READ_BACK_MIN_WIDTH}px` }}
			data-testid="remove-staff-summary"
			aria-live="polite"
		>
			<Typography
				component="span"
				sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: LETTER_SPACING.LABEL, textTransform: "uppercase", color: INK.MUTED }}
			>
				{t("remove.staff.summary.eyebrow")}
			</Typography>
			{summary.hours === null ? (
				line
			) : (
				<InHouseTooltip placement="top" arrow describeChild title={[summary.who, summary.date, summary.hours].join(FULL_LINE_SEPARATOR)}>
					{line}
				</InHouseTooltip>
			)}
		</Box>
	);
};

/** "↻ Recurring shift | Schedule template" — what the marker on a row's date cell means. */
const RecurringLegend: FC = () => {
	const { t } = useTranslation();
	return (
		<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flex: "none", fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
			<AutorenewOutlinedIcon aria-hidden sx={(theme) => ({ fontSize: FONT_SIZE.EMPHASIS, color: theme.palette.primary.main })} />
			{t("recurring.shift.master.schedule")}
		</Box>
	);
};

/**
 * One action row — the legend, the reason (asked once for every pick), a read-back of who comes off which shift,
 * and the buttons — like the old Remove Nurse footer, so the popup keeps its footprint. The read-back sits beside
 * the button on purpose: it is the confirmation. A picked reason's note / call-in fields add a second row.
 */
export const RemoveStaffFooter: FC<Props> = (props) => {
	const { t } = useTranslation();
	const { reasonOptions } = props.details;
	return (
		<Box
			component="footer"
			// While the reason's error hangs under the action row, a hint below it (the union warning, the reasons read
			// failing) starts under the error line instead of under the text. At rest, and with nothing below, the
			// footer's height is unchanged — the error still hangs into the bottom padding.
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: props.details.showReasonError ? ERROR_LINE_GAP : FOOTER_ROW_GAP,
				px: 3,
				pt: 2,
				pb: 2.5,
				borderTop: `1px solid ${NEUTRAL[200]}`,
			}}
			data-testid="remove-staff-footer"
		>
			<Box sx={ACTION_ROW_SX}>
				<RecurringLegend />
				<RemoveStaffReasonField details={props.details} />
				<RemovingReadBack summary={props.summary} selectedStaff={props.selectedStaff} />
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, marginInlineStart: "auto" }}>
					<SystemButton data-testid="remove-staff-cancel-button" buttonVariant="borderless" size="medium" onClick={props.onCancel} noFullWidthMobile>
						{t("cancel")}
					</SystemButton>
					<SystemButton
						data-testid="remove-staff-confirm-button"
						size="medium"
						onClick={props.onConfirm}
						disabled={!props.canSubmit}
						loading={props.confirmLoading}
						noFullWidthMobile
					>
						{props.confirmLabel}
					</SystemButton>
				</Box>
			</Box>
			<RemoveStaffExtraFields details={props.details} />
			{/* An empty picker is indistinguishable from a department with no reasons, and the removal can't
			    proceed without one — say the read failed rather than leaving a blank control. */}
			<InlineHint open={reasonOptions.isError} tone="warning" dataTestId="nurse-removal-reasons-error-hint">
				{t("error.general")}
			</InlineHint>
			<InlineHint open={props.unionWarning !== null} tone="warning" dataTestId="remove-staff-union-warning">
				{props.unionWarning}
			</InlineHint>
		</Box>
	);
};
